import React, { FC, useMemo } from 'react';
import { WebsiteHeaderLayout_PhotoBackground, WebsitePhotoCropInfo } from '@apps/guest/websiteHeader';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useGetContainerDimensions } from '@apps/guest/packages/layout-engine/widgets/Custom/hooks/useGetContainerDimensions';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { LazyScreen } from '@apps/guest/packages/layout-engine/widgets/Custom/components/LazyScreen/LazyScreen';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { EventNameNavForStandaloneHeader, EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { BrannanNavigation, EventPageType } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteEventDesign } from '../../../../layout.types';
import { CroppedProgressiveImage, ProgressiveImage } from '../components/ProgressiveImage';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

export type PhotoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_PhotoBackground;
    showSkeleton?: boolean;
    showPlaceholder?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
  } & EventNameNavForStandaloneHeaderProps
>;

type PhotoBackgroundRendererProps = Readonly<{
  photo?: Photo;
  height: string;
  containerDimensions: {
    width: number;
    height: number;
  };
  isCompactView: boolean;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
}>;

type Photo = Readonly<{
  id: string;
  url: string;
  cropInfo?: WebsitePhotoCropInfo;
}>;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;

const PhotoBackgroundRenderer: FC<PhotoBackgroundRendererProps> = props => {
  const { photo, containerDimensions, height, isCompactView, renderedContainerHeight, renderedContainerWidth, children } = props;

  const containerWidth = renderedContainerWidth ?? containerDimensions.width;
  const containerHeight = renderedContainerHeight ?? containerDimensions.height;

  const derivedWidth = containerHeight * CONTAINER_ASPECT_RATIO;
  const derivedHeight = containerWidth / CONTAINER_ASPECT_RATIO;

  const { bestSuitableWidth, bestSuitableHeight } =
    derivedWidth < containerWidth
      ? { bestSuitableWidth: containerWidth, bestSuitableHeight: derivedHeight }
      : { bestSuitableWidth: derivedWidth, bestSuitableHeight: containerHeight };

  if (photo?.cropInfo) {
    return (
      <AnimatedImageBox
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems={isCompactView ? 'flex-start' : 'center'}
        backgroundColor="lightgray"
      >
        <CroppedProgressiveImage
          width={isCompactView ? containerWidth : bestSuitableWidth}
          height={isCompactView ? derivedHeight : bestSuitableHeight}
          photoUrl={photo.url}
          cropInfo={{ ...photo.cropInfo, unit: '%' }}
          containerStyles={{ position: 'absolute' }}
        />
        <Box
          width="100%"
          height="100%"
          position={isCompactView ? 'relative' : 'absolute'}
          zIndex={1}
        >
          {children}
        </Box>
      </AnimatedImageBox>
    );
  }

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
      overflow="hidden"
      backgroundColor="lightgray"
      minHeight={pxToRem(96)}
    >
      {photo?.url && (
        <ProgressiveImage
          photoUrl={photo.url}
          width="100%"
          height="100%"
          containerStyles={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      )}
      {children}
    </AnimatedImageBox>
  );
};

export const PhotoBackgroundLayout: FC<PhotoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];

  const { containerDimensions, containerRef } = useGetContainerDimensions();

  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && currentPageType !== EventPageType.welcome && pageIndex !== 0;
  const isMobileOrTablet = useIsMobileOrTablet();

  // Hide photo background on mobile for non-welcome pages
  const shouldHidePhotoBackground = useMemo(() => {
    const isMobileView = forceRenderViewIn === 'mobile' || (forceRenderViewIn === undefined && isMobileOrTablet);
    return isCompactView && isMobileView;
  }, [isCompactView, forceRenderViewIn, isMobileOrTablet]);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : { height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : '100vh', aspectRatio: `${CONTAINER_ASPECT_RATIO}` },
    [isCompactView, isInlineEditing, renderedContainerHeight],
  );

  // Memoize container dimensions to prevent unnecessary style recalculations
  // when hiding photo background on mobile/tablet
  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHidePhotoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHidePhotoBackground ? 'auto' : height,
      containerMinHeight: shouldHidePhotoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHidePhotoBackground ? 'visible' : 'hidden',
    };
  }, [shouldHidePhotoBackground, aspectRatio, height]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  return (
    <Flex
      ref={containerRef}
      position="relative"
      zIndex={10}
      width="100%"
      aspectRatio={containerAspectRatio}
      height={containerHeight}
      minHeight={containerMinHeight}
      overflow={containerOverflow}
    >
      {shouldHidePhotoBackground ? (
        <EventNameNavForStandaloneHeader
          eventDisplayName={websiteHeader.eventDisplayName}
          colors={websiteHeader.headerPhoto.colors}
          headerTextData={websiteHeader.headerPhoto.eventName}
          isCompactView={isCompactView}
          currentPageType={currentPageType}
          handleEditTextClick={handleEditTextClick}
          isContentEditable={isInlineEditing}
          editableContentRef={editableContentRef}
          forceRenderViewIn={forceRenderViewIn}
          scaleValue={scaleValue}
          pages={pages}
          pageSlug={pageSlug}
          renderedContainerHeight={renderedContainerHeight}
          renderedContainerWidth={renderedContainerWidth}
          {...restProps}
        />
      ) : (
        <LazyScreen
          src={addRendition({ url: photo?.url, renditionSize: 'medium' })}
          width="100%"
          height={height}
          aspectRatio={aspectRatio}
          skeletonGroupOverrides={{
            Root: {
              props: {
                width: '100%',
                height: height,
                aspectRatio: aspectRatio,
              },
            },
            PlaceholderRoot: {
              props: {
                width: '100%',
                height: height,
                aspectRatio: aspectRatio,
                position: 'absolute',
              },
            },
          }}
          showSkeleton={showSkeleton}
        >
          {() => (
            <PhotoBackgroundRenderer
              photo={photo}
              containerDimensions={containerDimensions}
              height={height}
              isCompactView={isCompactView}
              renderedContainerHeight={renderedContainerHeight}
              renderedContainerWidth={renderedContainerWidth}
            >
              <EventNameNavForStandaloneHeader
                eventDisplayName={websiteHeader.eventDisplayName}
                colors={websiteHeader.headerPhoto.colors}
                headerTextData={websiteHeader.headerPhoto.eventName}
                isCompactView={isCompactView}
                currentPageType={currentPageType}
                handleEditTextClick={handleEditTextClick}
                isContentEditable={isInlineEditing}
                editableContentRef={editableContentRef}
                forceRenderViewIn={forceRenderViewIn}
                scaleValue={scaleValue}
                pages={pages}
                pageSlug={pageSlug}
                renderedContainerHeight={renderedContainerHeight}
                renderedContainerWidth={renderedContainerWidth}
                {...restProps}
              />
            </PhotoBackgroundRenderer>
          )}
        </LazyScreen>
      )}
    </Flex>
  );
};

PhotoBackgroundLayout.displayName = 'PhotoBackgroundLayout';
